import React, { useEffect } from "react";
import {
  Box,
  Divider,
  Radio,
  Stack,
} from "@mui/material";
import { DynamicModal } from "src/components/base/components/DynamicModal";
import { SSText } from "src/components/base/components/SSText";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { formatSSDate, pluralize } from "src/utils/utils";
import { TEvent } from "src/shared/types/globalStotre.types";
import { EventCard } from "src/components/feature/homepageFlow/EventCard";
import { AttendanceSeatDropdown } from "./AttendanceSeatDropdown";
import { useExchangeModal, useExchangeReservation } from "./useExchangeReservation";
import { useEventAttendance } from "./useEventAttendance";
import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";
import { calcEventRemainingSeatCount, getReservationSeatCap, pickCurrentMonthPass } from "./utils.event-attendance";

export function ExchangeReservationModal() {
  const theme = useSSTheme();
  const { HOME_PAGE_SETTINGS } = useTeamConfig();
  const exchangeModal = useExchangeModal();
  const { isReservationEnabled } = useEventAttendance();
  const { passesOnAccount } = useGlobalStore();
  const {
    eligibleReservations,
    selectedOldEventCode,
    setSelectedOldEventCode,
    seatCount,
    setSeatCount,
    isSubmitting,
    closeModal,
    executeExchange,
  } = useExchangeReservation();

  const newEvent = exchangeModal.newEvent;
  const exchangeDescription = HOME_PAGE_SETTINGS?.EXCHANGE_RESERVATION_MODAL?.DESCRIPTION;
  const exchangePaidDescription = newEvent?.isPaymentRequired
    ? HOME_PAGE_SETTINGS?.EXCHANGE_RESERVATION_MODAL?.PAID_DESCRIPTION
    : undefined;
  const passSeatCount = newEvent?.seatInformation?.PASS || 0;
  const currentMonthPass = pickCurrentMonthPass(passesOnAccount);
  const scopedPassSeatCount = currentMonthPass?.seatCount ?? passSeatCount;
  const reservationSeatCap = newEvent ? getReservationSeatCap(newEvent) : undefined;
  const eventRemainingSeatCount = newEvent
    ? calcEventRemainingSeatCount(newEvent)
    : Infinity;
  const maxSeatCount = Math.min(
    scopedPassSeatCount,
    reservationSeatCap ?? Infinity,
    eventRemainingSeatCount,
  );

  useEffect(() => {
    if (exchangeModal.isOpen) {
      setSeatCount(Math.min(passSeatCount, maxSeatCount) || 1);
      if (eligibleReservations.length === 1) {
        setSelectedOldEventCode(eligibleReservations[0].eventCode);
      }
    }
  }, [exchangeModal.isOpen]);

  const canContinue =
    Boolean(selectedOldEventCode) && seatCount >= 1 && !isSubmitting;

  const renderBodyContent = () => {
    if (eligibleReservations.length === 0) {
      return (
        <SSText
          variant="body"
          color={theme.mui.palette.text.secondary}
          fontSize="0.95rem"
          textAlign="center"
          py={3}
        >
          Exchange is unavailable — all your active reservations either have
          purchased guest tickets or were paid reservations that cannot be
          cancelled.
        </SSText>
      );
    }

    return (
      <>
        {/* Seat count selector */}
        <Stack mt={6} mb={3} spacing={3}>
          <Box width="100%" position="relative">
            <Divider />
            <SSText
              variant="body"
              fontWeight={700}
              fontSize="1.1rem"
              color={theme.mui.palette.grey[800]}
              sx={{
                position: "absolute",
                top: "50%",
                left: 0,
                transform: "translateY(-50%)",
                backgroundColor: theme.mui.palette.background.paper,
                pr: "10px",
                width: "max-content",
                whiteSpace: "nowrap",
              }}
            >
              Select Seat Count
            </SSText>
          </Box>
          <Stack spacing={1}>
            <SSText
              variant="body"
              fontWeight={500}
              fontSize="1rem"
              color={theme.mui.palette.grey[700]}
            >
              Select Number of Seats
            </SSText>
            <AttendanceSeatDropdown
              min={1}
              value={seatCount}
              max={maxSeatCount}
              onChange={setSeatCount}
            />
          </Stack>
        </Stack>

        {/* Reservation selection */}
        <Stack spacing={1}>
          <Box width="100%" position="relative">
            <Divider />
            <SSText
              variant="body"
              fontWeight={700}
              fontSize="1.1rem"
              color={theme.mui.palette.grey[800]}
              sx={{
                position: "absolute",
                top: "50%",
                left: 0,
                transform: "translateY(-50%)",
                backgroundColor: theme.mui.palette.background.paper,
                pr: "10px",
                width: "max-content",
                whiteSpace: "nowrap",
              }}
            >
              {eligibleReservations.length === 1
                ? "Reservation to Cancel"
                : "Select a Reservation to Cancel"}
            </SSText>
          </Box>

          <Stack spacing={1.5} pt={1}>
            {eligibleReservations.map((event) => (
              <ReservationOption
                key={event.eventCode}
                event={event}
                isSelected={selectedOldEventCode === event.eventCode}
                isSingleOption={eligibleReservations.length === 1}
                onSelect={() =>
                  eligibleReservations.length > 1 &&
                  setSelectedOldEventCode(event.eventCode)
                }
              />
            ))}
          </Stack>
        </Stack>
      </>
    );
  };

  return (
    <DynamicModal
      isOpen={exchangeModal.isOpen}
      onClose={closeModal}
      title="Exchange Reservation"
      hideTitleIcon
      slot={{
        secondaryButton: {
          label: "Cancel",
          variant: "outlined",
          isDisabled: isSubmitting,
          onClick: closeModal,
        },
        primaryButton: {
          label: "Confirm Exchange",
          isHidden: eligibleReservations.length === 0,
          isDisabled: !canContinue,
          isLoading: isSubmitting,
          onClick: () => executeExchange(isReservationEnabled),
        },
      }}
      description={
        <Stack sx={{ userSelect: "none" }}>
          {exchangeDescription && (
            <SSText variant="body1" color="text.secondary" mb={exchangePaidDescription ? 1 : 2}>
              {exchangeDescription}
            </SSText>
          )}
          {exchangePaidDescription && (
            <SSText variant="body1" color="text.secondary" mb={2}>
              {exchangePaidDescription}
            </SSText>
          )}
          {newEvent && <EventCard event={newEvent} hideTags showPriceBreakdown />}
          {renderBodyContent()}
        </Stack>
      }
    />
  );
}

interface ReservationOptionProps {
  event: TEvent;
  isSelected: boolean;
  isSingleOption: boolean;
  onSelect: () => void;
}

function ReservationOption({
  event,
  isSelected,
  isSingleOption,
  onSelect,
}: ReservationOptionProps) {
  const theme = useSSTheme();
  const seatCount = event.optedInSeatCount ?? 0;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      onClick={onSelect}
      sx={{
        border: `1px solid ${
          isSelected ? theme.colors.teamPrimary : theme.mui.palette.grey[200]
        }`,
        borderRadius: 2,
        p: 1.5,
        cursor: isSingleOption ? "default" : "pointer",
        backgroundColor: isSelected
          ? `${theme.colors.teamPrimary}10`
          : theme.mui.palette.background.paper,
        transition: "border-color 0.15s, background-color 0.15s",
        "&:hover": isSingleOption ? {} : { borderColor: theme.colors.teamPrimary },
      }}
    >
      {!isSingleOption && (
        <Radio
          checked={isSelected}
          onChange={onSelect}
          size="small"
          sx={{
            p: 0,
            color: theme.colors.teamPrimary,
            "&.Mui-checked": { color: theme.colors.teamPrimary },
          }}
        />
      )}
      <Stack spacing={0.25} flex={1}>
        <SSText variant="body" fontWeight={600} fontSize="0.95rem">
          {event.title}
        </SSText>
        <Stack direction="row" alignItems="center" sx={{ opacity: 0.7 }}>
          <SSText variant="body" fontSize="0.9rem" fontWeight={500}>
            {formatSSDate(event.date ?? "", "EEEE, MMM dd")}
          </SSText>
          <Divider
            orientation="vertical"
            sx={{ height: "0.8rem", mx: 0.8, backgroundColor: "currentColor" }}
          />
          <SSText variant="body" fontSize="0.9rem" fontWeight={500}>
            {event.time}
          </SSText>
        </Stack>
        <SSText
          variant="body"
          fontSize="0.85rem"
          color={theme.mui.palette.text.secondary}
        >
          {seatCount} {pluralize("seat", seatCount)}
        </SSText>
      </Stack>
    </Stack>
  );
}
